/**
 * Policy API Service
 * 
 * This service provides API client methods for policy management operations including:
 * - CRUD operations for policies
 * - Document management (upload, download, delete)
 * - Commission payment tracking
 * - Policy status updates
 * - Policy renewal
 * - Dashboard metrics
 * - Import/Export functionality
 * 
 * Requirements: Policy Management Frontend Design Document
 */

import apiClient from './api';
import {
  Policy,
  PolicyDetail,
  PolicyFormData,
  PolicyRenewalCreate,
  CommissionPaymentUpdate,
  PolicyStatusUpdate,
  PolicyDocument,
  DashboardMetrics,
  PaginatedResponse,
} from '../types';
import { ImportResult, ImportRowResult } from '../types/importExport';

export interface CustomerPolicyRow {
  id: string;
  policy_number: string;
  policy_type: string;
  insurance_category?: string;
  insurance_category_label: string;
  status: 'active' | 'expired' | 'cancelled';
  days_until_expiry?: number;
  is_nearing_expiry?: boolean;
  is_cancelled?: boolean;
  customer_id: string;
  customer_name: string;
  company_id?: string;
  company_name?: string;
  agent_id?: string;
  agent_name?: string;
  team_id?: string;
  team_name?: string;
  premium?: number;
  commission_rate?: number;
  sum_insured?: number;
  start_date: string;
  end_date: string;
  license_plate?: string;
  insured_property_address?: string;
  category_specific_info?: string;
  main_contract_name?: string;
  main_contract_details?: string;
  has_compulsory?: boolean;
  compulsory_policy_number?: string;
  endorsement?: string;
  policy_details?: string;
  notes?: string;
  quotation_id?: string;
  created_at: string;
  updated_at?: string;
}

export interface PolicyAttachmentRecord {
  id: string;
  filename: string;
  file_size: number;
  content_type: string;
  uploaded_at: string;
}

export interface CustomerPolicyDetail extends CustomerPolicyRow {
  commission?: {
    id: string;
    status: string;
    commission_amount?: number;
    agent_commission?: number;
    paid_at?: string;
    payment_reference?: string;
  };
  attachments: PolicyAttachmentRecord[];
}

const policyService = {
  /**
   * Get paginated list of policies with optional search and filters
   * 
   * @param skip - Number of records to skip (for pagination)
   * @param limit - Maximum number of records to return
   * @param search - Search term for policy number, customer name, or agent name
   * @param policyType - Filter by policy type
   * @param status - Filter by policy status
   * @param commissionStatus - Filter by commission payment status
   * @param startDateFrom - Filter by start date from
   * @param startDateTo - Filter by start date to
   * @returns Paginated list of policies
   */
  getPolicies: async (
    skip: number = 0,
    limit: number = 50,
    search?: string,
    policyType?: string,
    status?: string,
    commissionStatus?: string,
    startDateFrom?: string,
    startDateTo?: string
  ): Promise<PaginatedResponse<Policy>> => {
    const params: any = { skip, limit };
    if (search) params.search = search;
    if (policyType) params.policy_type = policyType;
    if (status) params.status = status;
    if (commissionStatus) params.commission_status = commissionStatus;
    if (startDateFrom) params.start_date_from = startDateFrom;
    if (startDateTo) params.start_date_to = startDateTo;

    const response = await apiClient.get('/policies', { params });
    return response.data;
  },

  /**
   * Get policy details with documents and status history
   * 
   * @param id - Policy ID
   * @returns Detailed policy information including documents and history
   */
  getPolicy: async (id: string): Promise<PolicyDetail> => {
    const response = await apiClient.get(`/policies/${id}`);
    return response.data;
  },

  /**
   * Create a new policy
   * 
   * @param data - Policy creation data
   * @returns Created policy
   */
  createPolicy: async (data: PolicyFormData): Promise<Policy> => {
    const response = await apiClient.post('/policies', data);
    return response.data;
  },

  /**
   * Update policy information
   * 
   * @param id - Policy ID
   * @param data - Policy update data (partial)
   * @returns Updated policy
   */
  updatePolicy: async (id: string, data: Partial<PolicyFormData>): Promise<Policy> => {
    const response = await apiClient.put(`/policies/${id}`, data);
    return response.data;
  },

  /**
   * Soft delete a policy
   * 
   * @param id - Policy ID
   */
  deletePolicy: async (id: string): Promise<void> => {
    await apiClient.delete(`/policies/${id}`);
  },

  /**
   * Update policy status
   * 
   * @param id - Policy ID
   * @param data - Status update data including new status and optional reason
   * @returns Updated policy
   */
  updatePolicyStatus: async (id: string, data: PolicyStatusUpdate): Promise<Policy> => {
    const response = await apiClient.put(`/policies/${id}/status`, data);
    return response.data;
  },

  /**
   * Update commission payment information
   * 
   * @param id - Policy ID
   * @param data - Commission payment update data
   * @returns Updated policy
   */
  updateCommissionPayment: async (id: string, data: CommissionPaymentUpdate): Promise<Policy> => {
    const response = await apiClient.put(`/policies/${id}/commission`, data);
    return response.data;
  },

  /**
   * Renew a policy
   * 
   * Creates a new policy based on an existing policy with optional overrides.
   * Links the original and renewed policies bidirectionally.
   * 
   * @param data - Renewal data including original policy ID and optional overrides
   * @returns Newly created renewal policy
   */
  renewPolicy: async (data: PolicyRenewalCreate): Promise<Policy> => {
    const response = await apiClient.post(`/policies/${data.original_policy_id}/renew`, data);
    return response.data;
  },

  /**
   * Upload policy document
   * 
   * @param policyId - Policy ID
   * @param file - File to upload (PDF, JPG, JPEG, PNG)
   * @returns Uploaded document information
   */
  uploadDocument: async (policyId: string, file: File): Promise<PolicyDocument> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(`/policies/${policyId}/documents`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Download policy document
   * 
   * Downloads the document file and triggers browser download.
   * 
   * @param policyId - Policy ID
   * @param documentId - Document ID
   */
  downloadDocument: async (policyId: string, documentId: string): Promise<Blob> => {
    const response = await apiClient.get(`/policies/${policyId}/documents/${documentId}`, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data], { type: response.data.type });

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'document';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    // Trigger browser download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return blob;
  },

  /**
   * Delete policy document
   * 
   * Soft deletes the document (marks as deleted but keeps in database).
   * 
   * @param policyId - Policy ID
   * @param documentId - Document ID
   */
  deleteDocument: async (policyId: string, documentId: string): Promise<void> => {
    await apiClient.delete(`/policies/${policyId}/documents/${documentId}`);
  },

  /**
   * Get dashboard metrics
   * 
   * Retrieves summary statistics about policies including:
   * - Active policies count
   * - Monthly premium total
   * - Unpaid commission total
   * - Policies nearing expiry count
   * - Policies by type distribution
   * - Premium trends (last 12 months)
   * - Top agents by premium
   * 
   * @returns Dashboard metrics
   */
  getDashboardMetrics: async (): Promise<DashboardMetrics> => {
    const response = await apiClient.get('/dashboard/metrics');
    return response.data;
  },

  /**
   * Export policies to Excel
   * 
   * Downloads an Excel file containing all policies matching the current filters.
   * The file includes all policy fields formatted for Thai users with Buddhist calendar dates.
   * 
   * @param search - Search term
   * @param policyType - Filter by policy type
   * @param status - Filter by policy status
   * @param commissionStatus - Filter by commission status
   * @param startDateFrom - Filter by start date from
   * @param startDateTo - Filter by start date to
   */
  exportPolicies: async (
    search?: string,
    policyType?: string,
    status?: string,
    commissionStatus?: string,
    startDateFrom?: string,
    startDateTo?: string
  ): Promise<void> => {
    const params: any = {};
    if (search) params.search = search;
    if (policyType) params.policy_type = policyType;
    if (status) params.status = status;
    if (commissionStatus) params.commission_status = commissionStatus;
    if (startDateFrom) params.start_date_from = startDateFrom;
    if (startDateTo) params.start_date_to = startDateTo;

    const response = await apiClient.get('/policies/export', {
      params,
      responseType: 'blob',
    });

    // Create blob URL and trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `policies_${new Date().toISOString().split('T')[0]}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  /**
   * Import policies from Excel/CSV
   * 
   * Uploads an Excel or CSV file containing policy data and creates policies in bulk.
   * Returns a summary of successful and failed imports with detailed error messages.
   * 
   * @param file - Excel or CSV file to import
   * @returns Import result with success/failure counts and error details
   */
  importPolicies: async (file: File): Promise<ImportResult> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post('/policies/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Download annotated XLSX template for policy import (from backend)
   */
  downloadImportTemplate: async (): Promise<void> => {
    const response = await apiClient.get('/policies/import-template', { responseType: 'blob' });
    downloadBlob(response.data, 'policy_import_template.xlsx');
  },

  /**
   * Get paginated list of CustomerPolicy records
   */
  getCustomerPolicies: async (
    skip: number = 0,
    limit: number = 50,
    search?: string,
    policyType?: string,
    insuranceCategory?: string,
    insuranceCompanyId?: string,
    startDateFrom?: string,
    startDateTo?: string,
  ): Promise<PaginatedResponse<CustomerPolicyRow>> => {
    const params: any = { skip, limit };
    if (search) params.search = search;
    if (policyType) params.policy_type = policyType;
    if (insuranceCategory) params.insurance_category = insuranceCategory;
    if (insuranceCompanyId) params.insurance_company_id = insuranceCompanyId;
    if (startDateFrom) params.start_date_from = startDateFrom;
    if (startDateTo) params.start_date_to = startDateTo;
    const response = await apiClient.get('/customer-policies', { params });
    return response.data;
  },

  /**
   * Export CustomerPolicy records to Excel
   */
  exportCustomerPolicies: async (
    search?: string,
    policyType?: string,
    insuranceCategory?: string,
    insuranceCompanyId?: string,
    startDateFrom?: string,
    startDateTo?: string,
  ): Promise<void> => {
    const params: any = {};
    if (search) params.search = search;
    if (policyType) params.policy_type = policyType;
    if (insuranceCategory) params.insurance_category = insuranceCategory;
    if (insuranceCompanyId) params.insurance_company_id = insuranceCompanyId;
    if (startDateFrom) params.start_date_from = startDateFrom;
    if (startDateTo) params.start_date_to = startDateTo;
    const response = await apiClient.get('/customer-policies/export', { params, responseType: 'blob' });
    downloadBlob(response.data, `customer_policies_${new Date().toISOString().split('T')[0]}.xlsx`);
  },

  /**
   * Soft-delete a CustomerPolicy record (Admin/Manager only)
   */
  deleteCustomerPolicy: async (id: string): Promise<void> => {
    await apiClient.delete(`/customer-policies/${id}`);
  },

  /**
   * Get full CustomerPolicy detail (commission, attachments, all fields)
   */
  getCustomerPolicy: async (id: string): Promise<CustomerPolicyDetail> => {
    const response = await apiClient.get(`/customer-policies/${id}`);
    return response.data;
  },

  /**
   * Manually create a CustomerPolicy record
   */
  createCustomerPolicy: async (data: Partial<CustomerPolicyRow> & {
    customer_id: string;
    policy_number: string;
    policy_type: string;
    start_date: string;
    end_date: string;
    commission_rate?: number;  // 0.0–1.0  (e.g. 0.15 = 15%)
  }): Promise<CustomerPolicyDetail> => {
    const response = await apiClient.post('/customer-policies', data);
    return response.data;
  },

  /**
   * Update an existing CustomerPolicy record
   */
  updateCustomerPolicy: async (id: string, data: Partial<CustomerPolicyRow> & {
    commission_rate?: number;  // 0.0–1.0
  }): Promise<CustomerPolicyDetail> => {
    const response = await apiClient.put(`/customer-policies/${id}`, data);
    return response.data;
  },

  /**
   * Upload a document to a CustomerPolicy
   */
  uploadCustomerPolicyDocument: async (policyId: string, file: File): Promise<PolicyAttachmentRecord> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
      `/customer-policies/${policyId}/documents`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  /**
   * Download a document attachment
   */
  downloadCustomerPolicyDocument: async (policyId: string, docId: string): Promise<Blob> => {
    const response = await apiClient.get(
      `/customer-policies/${policyId}/documents/${docId}`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  /**
   * Delete a document attachment
   */
  deleteCustomerPolicyDocument: async (policyId: string, docId: string): Promise<void> => {
    await apiClient.delete(`/customer-policies/${policyId}/documents/${docId}`);
  },

  /**
   * Retry a single failed import row
   */
  retryImportRow: async (rawData: Record<string, unknown>): Promise<ImportRowResult> => {
    const formData = new FormData();
    // Convert single row to a minimal CSV and upload as a 1-row file
    const headers = Object.keys(rawData).join(',');
    const values  = Object.values(rawData).map(v => String(v ?? '')).join(',');
    const csv     = `${headers}\n${values}`;
    const blob    = new Blob([csv], { type: 'text/csv' });
    formData.append('file', blob, 'retry.csv');
    const response = await apiClient.post('/policies/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    // Return just the first result row
    return response.data.results?.[0] ?? response.data;
  },
};

// ── Utility: trigger a browser download from a Blob ──────────────────────────
function downloadBlob(blob: Blob, filename: string): void {
  const url  = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href  = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export { downloadBlob };
export default policyService;
